import { computed, toValue } from "vue";
import { toBoolean, toJSON } from "#ce/utils/props";
import { t } from "#ce/shared/components-i18n";
import { ANY_OPTION, toRowValue } from "#ce/shared/filters/category/row-values";

/**
 * Turns a `CategoryFilter`'s `props.items` into the rows `sp-quantum-select` renders, narrowed to
 * the ids `scope` admits.
 *
 * @param {Object} props - The component's `props` (`navigation`, `items`, `anyLabel`,
 *   `anyOptionTestId`, `iconBadge`).
 * @param {import("vue").Ref<Array<String>>} selectedIds - The ids currently selected, kept offered
 *   even after scope has moved on without them.
 * @param {Object} scope - `isScoped` and `isInScope` from `useCategoryFilterScope`, and `offersAny`
 *   from `useCategoryFilterSelection`.
 * @returns {Object}
 *   @returns {import("vue").ComputedRef<Boolean>} isNested - Whether `navigation` nests children.
 *   @returns {import("vue").ComputedRef<Object>} branchAttrs - `sp-quantum-select` attrs for a
 *     nested tree's branch rows.
 *   @returns {import("vue").ComputedRef<Array>} models - The offered rows and section headers.
 *   @returns {import("vue").ComputedRef<Map>} nodesById - Every node keyed by its id, for reading a
 *     selected node's name.
 */
export function useCategoryFilterItems(props, selectedIds, { isScoped, isInScope, offersAny }) {
  const isNested = computed(() => props.navigation !== "flat");

  // A branch row is itself a selection; its disclosure toggle is the one that expands it.
  const branchAttrs = computed(() => {
    if (!isNested.value) {
      return {};
    }

    return {
      "selectable-branches": true,
      "start-on-root": true,
      "branch-toggle-label": t("finder_filter_toggle_branch"),
    };
  });

  const items = computed(() => {
    const parsed = typeof props.items === "string" ? toJSON(props.items, true) : props.items;

    return Array.isArray(parsed) ? parsed : [];
  });

  function childrenOf(item) {
    return Array.isArray(item.children) ? item.children : [];
  }

  // A node stays in scope while it, one of its descendants, or its own selection is; the selection
  // keeps a restored deep link nameable after the scope moved on without it.
  function scopedNode(item) {
    const children = childrenOf(item).map(scopedNode).filter(Boolean);
    const own = isInScope(item.id) || selectedIds.value.includes(String(item.id));

    if (!own && children.length === 0) {
      return undefined;
    }

    return childrenOf(item).length > 0 ? { ...item, children } : item;
  }

  const offeredItems = computed(() => (isScoped.value ? items.value.map(scopedNode).filter(Boolean) : items.value));

  // Under `icon-badge` the glyph sits on a themeable circle, shared with the omnisearch's row icons.
  function toPrependIcon(name) {
    if (!toBoolean(props.iconBadge)) {
      return name;
    }

    return {
      name,
      color: "var(--sp-comp-category-filter-icon-badge-color, #fff)",
      background: {
        shape: "circle",
        color:
          "var(--sp-comp-category-filter-icon-badge-background, var(--sp-comp-omni-search-default-icon-bg, #b2d9dc))",
      },
    };
  }

  // `occurrences` counts the rows already built per id, so a repeated id's rows stay distinct and
  // the picker marks the first alone.
  function toRow(item, occurrences) {
    const id = String(item.id);
    const occurrence = occurrences.get(id) ?? 0;
    occurrences.set(id, occurrence + 1);

    const row = { title: item.name, value: toRowValue(id, occurrence) };

    if (item.icon) {
      row.props = { prependIcon: toPrependIcon(item.icon) };
    }
    if (item.visibility != null) {
      row.visibility = item.visibility;
    }

    const children = childrenOf(item);

    if (children.length > 0) {
      row.children = children.map((child) => toRow(child, occurrences));
      row.hasChildren = true;
    }

    return row;
  }

  // A top-level node's `group` is its section header; a child renders under its parent instead.
  function toTopLevelRow(item, occurrences) {
    const row = toRow(item, occurrences);

    if (item.group != null) {
      row.group = item.group;
    }

    return row;
  }

  // The clearing row carries its own test id, so a test picking an option row never lands on it.
  function anyRow() {
    const row = { title: props.anyLabel, value: ANY_OPTION };

    if (props.anyOptionTestId) {
      row.props = { "data-testid": props.anyOptionTestId };
    }

    return row;
  }

  // The clearing row leads the list and is outside the scope, so a scope that admits no option
  // still leaves a way out. A filter with no items offers nothing at all.
  const models = computed(() => {
    const occurrences = new Map();
    const rows = offeredItems.value.map((item) => toTopLevelRow(item, occurrences));
    const ungroupedFirst = [...rows.filter((row) => !("group" in row)), ...rows.filter((row) => "group" in row)];
    const ordered = isNested.value ? withSectionHeaders(ungroupedFirst) : ungroupedFirst;

    if (!toValue(offersAny) || items.value.length === 0) {
      return ordered;
    }

    return [anyRow(), ...ordered];
  });

  // Match Uber Select: ungrouped rows first, then sections in their original order.
  // Explicit headers preserve that order through the select's auto-grouping.
  // A whitespace header ends the prior ARIA group without displaying another heading.
  function withSectionHeaders(rows) {
    let previousGroup;
    return rows.flatMap((row) => {
      const startsGroup = row.group !== previousGroup;
      previousGroup = row.group;
      return startsGroup ? [{ header: row.group ?? " " }, row] : [row];
    });
  }

  // Every node of the tree, parents before their children.
  function flattenNodes(nodes) {
    return nodes.flatMap((node) => [node, ...flattenNodes(childrenOf(node))]);
  }

  const nodesById = computed(() => new Map(flattenNodes(items.value).map((node) => [String(node.id), node])));

  return { isNested, branchAttrs, models, nodesById };
}
